/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import { AttachmentItemHolder } from '@nile/banyan/src/Components/Editors/RTEditor';
import {
  ALL_MODULES,
  ApiProgressLoader,
  AttachmentPreviewer,
  DateTimeFormatter,
  generateString,
  getDownloadURL,
  getTranslatedText,
  withErrorBoundary
} from '@nile/components';
import {
  dompurifier,
  getCurrentUserHook
} from '@nile/components/src/Utils/helpers';
import {
  GET_VENDOR_CONVERSATION_THREADED_CONTENT,
  RESEND_VENDOR_CONVERSATION,
  AccountClient
} from '@nile/graphql/src';
import envConfig from '@nile/home/src/Config/env';
import Recepients from '@nile/mangifera/src/Components/Chat/Recepients';
import DisplayOriginalMessage from '@nile/mangifera/src/Components/Sheets/DisplayOriginalMessage';
import { Receiver, Sender } from '@nile/mangifera/src/Pebbles/chat';
import { SVGIcon } from '@superops/super-icons';
import {
  Avatar,
  Button,
  Card,
  DisplayText,
  Link,
  Popover,
  TextStyle
} from '@superops/ui-design-system';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Tooltip } from 'react-tippy';
import { Tooltip as CloneTippy } from 'react-clone-tippy';
import AttachmentHolder from '@nile/rmm/src/modules/common/components/sub-pages/attachment-holder';
import { useMutation } from '@apollo/client';

const errorStatuses = ['BOUNCED', 'DROPPED', 'BLOCKED', 'FAILURE'];

const ActionPopover = (props) => {
  const { conversation, module, workItemId, readOnly } = props;
  const [popup, setPopup] = useState(false);

  const togglePopup = useCallback(() => {
    setPopup(!popup);
  });

  const activator = (
    <div onClick={togglePopup} className="ml-5 mt3">
      <div className="flex">
        <SVGIcon name="Menu" size="sm" />
      </div>
    </div>
  );

  if (readOnly) return null;

  return (
    <div className="visibilityActions pointer" id="bubble-actions">
      <Popover
        active={popup}
        activator={activator}
        onClose={() => {
          togglePopup();
        }}
      >
        <DisplayOriginalMessage
          query={GET_VENDOR_CONVERSATION_THREADED_CONTENT}
          responseKey="getVendorConversationThreadedContent"
          input={{
            workItem: {
              workId: workItemId,
              module: module
            },
            conversation: {
              conversationId:
                conversation?.parentConversationId ??
                conversation?.conversationId
            }
          }}
        />
      </Popover>
    </div>
  );
};

const TooltipWrapper = ({ children, html }) => {
  if (html) {
    return (
      <CloneTippy
        html={html}
        delay={300}
        duration={250}
        position="bottom-start"
        inertia
        trigger="mouseenter"
        animation="shift"
        animateFill={false}
        theme="super-light"
      >
        {children}
      </CloneTippy>
    );
  } else {
    return children;
  }
};

function SideConversationBubble(props) {
  const {
    intl,
    message: conversation,
    workItem,
    workItemId,
    module,
    company,
    clientId,
    readOnly,
    backgroundThreadConversation,
    sentMessageData,
    updateConversationsFromSocket
  } = props;
  const { userType } = conversation;
  const domain = window.location.host;
  const flags = useFlags();

  const currentUser = getCurrentUserHook();
  const initialConversationStats = conversation?.outgoingMailStatus;
  const Tags = {
    sender: Sender,
    receiver: Receiver
  };
  const Tag = Tags[userType];
  const isSender = userType === 'sender';
  const isMyChat = currentUser?.userId === conversation?.user?.userId;

  const [conversationStatus, setConversationStatus] = useState(
    initialConversationStats || null
  );

  const transparentBackground = backgroundThreadConversation
    ? !conversationStatus || conversationStatus !== 'SUCCESS'
    : false;

  const [mailFailureReason, setMailFailureReason] = useState(
    conversation?.outgoingMailFailureReason
  );
  const isFailureConversation = errorStatuses.includes(conversationStatus);
  const [showLoader, setShowLoader] = useState(
    isMyChat
      ? initialConversationStats === 'PROGRESS' ||
          initialConversationStats === 'IN_QUEUE'
      : false
  );

  const getLoader = () => {
    const convStatus = conversationStatus;
    const status = errorStatuses.includes(convStatus) ? 'FAILURE' : convStatus;
    switch (status) {
      case 'SUCCESS':
        return <ApiProgressLoader isSuccess />;
      case 'FAILURE':
        return <ApiProgressLoader isSuccess={false} />;
      default:
        return <ApiProgressLoader isLoading />;
    }
  };

  const getStatusMessageKey = () => {
    const convStatus = conversationStatus;
    const status = errorStatuses.includes(convStatus) ? 'FAILURE' : convStatus;
    switch (status) {
      case 'SUCCESS':
        return 'ticket.details.emailNotification.status.success';
      case 'FAILURE':
        return 'ticket.details.emailNotification.status.notDelivered';
      default:
        return 'ticket.details.emailNotification.status.progress';
    }
  };

  const [
    resendVendorConversation,
    { loading: resendTicketConversationLoading }
  ] = useMutation(RESEND_VENDOR_CONVERSATION, {
    client: AccountClient,
    onCompleted: (res) => {},
    onError() {
      setConversationStatus('FAILURE');
      setShowLoader(false);
    }
  });

  const resendConversation = () => {
    if (workItemId && conversation?.conversationId) {
      setConversationStatus('PROGRESS');
      setShowLoader(true);
      resendVendorConversation({
        variables: {
          input: {
            workItem: {
              workId: workItemId,
              module: module
            },
            conversation: {
              conversationId: conversation?.conversationId
            }
          }
        }
      });
    }
  };

  const [randomId, setRandomId] = useState(generateString(5) || 'abcde');
  const [showAttachmentPreview, setShowAttachmentPreview] = useState({
    show: false,
    index: 0
  });
  const [attachmentsList, setAttachments] = useState([]);

  const htmlString = dompurifier(conversation.message);

  const htmlToDisplay = htmlString.replaceAll('<img', "<img loading='lazy'");

  const getAttachmentURL = (item) => {
    const details = {
      fileName: item?.fileName,
      conversationId: conversation?.conversationId,
      workItem: {
        workId: workItemId,
        module: module
      }
    };
    const url = getDownloadURL({
      module: ALL_MODULES.sideConversationAttachment,
      details
    });
    return url;
  };

  useEffect(() => {
    let attachments = [];
    if (
      conversation &&
      conversation.attachments &&
      conversation.attachments.length
    )
      attachments = conversation.attachments.map((item) => {
        return {
          name: item?.fileName,
          type: item?.fileName?.substr(
            item?.fileName.lastIndexOf('.') + 1,
            item?.fileName.length
          ),
          url: getAttachmentURL(item)
        };
      });
    setAttachments(attachments);
  }, [conversation.attachments]);

  const handleEscPress = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (e?.keyCode === 27 || e?.which === 27 || e?.key === 'Escape') {
      if (
        window.getComputedStyle(document.querySelector(`.modal-inline-images`))
          .display === 'flex'
      ) {
        document.querySelector(`.modal-inline-images`).style.display = 'none';
      }
      window.removeEventListener('keyup', handleEscPress);
    }
  };

  const imageDownloader = (e) => {
    // window.open(e.target.currentSrc, '_blank').focus();
    const modal = document.querySelector(`.modal-inline-images`);

    const modalImg = document.querySelector(`.modal-content-inline-images`);

    modal.style.display = 'flex';
    modalImg.src = e.target.currentSrc;
    window.addEventListener('keyup', handleEscPress);
  };

  useEffect(() => {
    if (
      document.querySelector(`.${randomId}`)?.querySelectorAll('img')?.length
    ) {
      document
        ?.querySelector(`.${randomId}`)
        ?.querySelectorAll('img')
        ?.forEach((img) => {
          img.classList.add('cursor-zoom-in', 'mw100');
          img.addEventListener('click', imageDownloader);
        });
      return () => {
        document
          ?.querySelector(`.${randomId}`)
          ?.querySelectorAll('img')
          ?.forEach((img) => {
            img.classList.remove('cursor-zoom-in');
            img.removeEventListener('click', imageDownloader);
          });
      };
    }
  }, []);

  useEffect(() => {
    if (
      sentMessageData &&
      sentMessageData?.conversationId === conversation?.conversationId &&
      (sentMessageData?.outgoingMailStatus !== conversationStatus ||
        sentMessageData?.outgoingMailFailureReason !== mailFailureReason)
    ) {
      const updatedStatus = sentMessageData?.outgoingMailStatus;
      const failureReason = sentMessageData?.outgoingMailFailureReason || null;
      setConversationStatus(updatedStatus);
      setMailFailureReason(failureReason);

      if (
        updatedStatus === 'SUCCESS' ||
        errorStatuses.includes(updatedStatus)
      ) {
        setTimeout(() => {
          setShowLoader(false);
        }, 500);
      }

      setTimeout(() => {
        updateConversationsFromSocket(sentMessageData);
      }, 500);
    }
  }, [sentMessageData]);

  /**
   * @description
   * opening attachment previewer, if selected from a specific index then opening from the selected index.
   * @param {object} attachment
   * @param {number} index
   */

  function openAttachmentPreview(index) {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: true,
      index
    });
  }

  /**
   * @description
   * Closing attachment previewer
   */
  const closePreviwer = () => {
    setShowAttachmentPreview({
      ...showAttachmentPreview,
      show: false
    });
  };

  const getUserName = (message) => {
    if (message?.user?.name || message?.user?.email) {
      return `${message.user.name || message.user.email || ''}`;
    }
    return getTranslatedText(intl, 'ticket.details.note.system');
  };

  return (
    <div className="mb12">
      <Tag className="flex rel bubbleContainer position-relative bubble-wrapper g10 visibilityContainer">
        <div className="avatar position-relative">
          <Avatar
            size="small"
            initials={getUserName(conversation)[0] || ''}
            name={getUserName(conversation)}
            source={
              !(conversation?.user?.name || conversation?.user?.email)
                ? `${envConfig.imageRoot}svg/notification.svg`
                : ''
            }
          />
        </div>
        <div id="side-conversation-bubble-outer" className="w100">
          <Card
            variant={
              transparentBackground && backgroundThreadConversation
                ? 'none'
                : isSender
                ? 'discussion'
                : 'card'
            }
            bubbleAlignment={isSender ? 'topRight' : 'topLeft'}
            rounded
          >
            <div className="side-conversation-bubble word-break pt20 pr10 pb20 pl20">
              <div className="flex f-12">
                <div className="reply-info mcw-30 f-12 fx actr no-scroll grow-2 g3">
                  <span className="cs actr">
                    <span className="fx actr">
                      <SVGIcon
                        size="sm"
                        name={userType === 'sender' ? 'MailSettings' : 'Reply'}
                      />
                    </span>
                    <div className="w100 mw-fc">
                      <FormattedMessage
                        id={
                          userType === 'sender mcw-100'
                            ? 'client.details.emailedby'
                            : 'client.details.repliedBy'
                        }
                      />
                    </div>

                    <span className="sender fx actr g3 ellipsis">
                      <Tooltip
                        html={getUserName(conversation)}
                        delay={300}
                        duration={250}
                        position="top"
                        inertia
                        trigger="mouseenter"
                        animation="shift"
                        animateFill={false}
                        className="bold email mcw-20 ellipsis nowrap w100 mw-fc"
                      >
                        {getUserName(conversation)}
                      </Tooltip>
                      <Tooltip
                        html={
                          <Recepients
                            bubbleContainer
                            message={{ ...conversation, bccUsers: [] }}
                          />
                        }
                        delay={300}
                        duration={250}
                        position="bottom-start"
                        inertia
                        trigger="mouseenter"
                        animation="shift"
                        animateFill={false}
                        theme="super-light"
                        className="flex-imp"
                      >
                        <SVGIcon name="DropSheet" size="sm" />
                      </Tooltip>
                    </span>
                  </span>
                </div>
                {(conversationStatus === 'SUCCESS' ||
                  !backgroundThreadConversation) && (
                  <>
                    <div className="fx2 flex jend f-12">
                      <TextStyle variation="subdued">
                        {dayjs(conversation.time).format(
                          `${company?.dateFormat ||
                            'MMM DD YYYY'}, ${company?.timeFormat || 'hh:mm A'}`
                        )}
                      </TextStyle>
                    </div>
                    <ActionPopover
                      conversation={conversation}
                      workItemId={workItemId}
                      workItem={workItem}
                      module={module}
                      readOnly={readOnly}
                    />
                  </>
                )}

                {backgroundThreadConversation && (
                  <TooltipWrapper
                    html={
                      mailFailureReason && (
                        <div className="p10 rounded-4px fx fcol g5 bshadow">
                          <div className="fx fcol g3">
                            <DisplayText size="medium" weight="Bold">
                              {getTranslatedText(intl, 'common.error')}
                            </DisplayText>
                            <DisplayText weight="Regular" size="small">
                              <TextStyle variation="subdued">
                                <DateTimeFormatter
                                  type="dateTime"
                                  date={conversation?.time}
                                />
                              </TextStyle>
                            </DisplayText>
                          </div>
                          <DisplayText>
                            {getTranslatedText(
                              intl,
                              `app.errorCode.${mailFailureReason?.errorCode}`
                            )}
                          </DisplayText>
                        </div>
                      )
                    }
                  >
                    <div className="flex g5 actr">
                      {(showLoader || !conversation?.outgoingMailStatus) && (
                        <div className="flex g5 actr">
                          <div className="flex">{getLoader()}</div>

                          <div className="flex">
                            <TextStyle variation="subdued">
                              <FormattedMessage id={getStatusMessageKey()} />
                            </TextStyle>
                          </div>
                        </div>
                      )}

                      {isFailureConversation && (
                        <div className="flex  actr">
                          <div className="flex g5 actr">
                            <div className="flex">
                              <SVGIcon name="WarningOutline" color="red" />
                            </div>
                            <div>
                              <TextStyle variation="subdued">
                                <FormattedMessage
                                  id={getStatusMessageKey(conversationStatus)}
                                />
                              </TextStyle>
                            </div>
                          </div>

                          {isMyChat && (
                            <div>
                              <Button
                                plain
                                onClick={resendConversation}
                                size="slim"
                              >
                                <span className="fsize12">Retry</span>
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </TooltipWrapper>
                )}
              </div>
              <p
                className={`message-content ${randomId}`}
                dangerouslySetInnerHTML={{
                  __html: htmlToDisplay
                }}
              />
              {conversation?.minifiedContent && (
                <a
                  href={`https://${domain}/#/minify/${module?.toLowerCase()}/vendorconversation/${workItemId}/${
                    conversation?.conversationId
                  }`}
                  target="_blank"
                >
                  View All
                </a>
              )}
              <div className="flex g10 mt-10 wrap">
                {conversation?.attachments?.map((item, index) => {
                  return (
                    <AttachmentHolder
                      onClick={() =>
                        openAttachmentPreview(
                          index,
                          conversation?.attachments,
                          item
                        )
                      }
                      fileName={item?.fileName || ''}
                      fontWeight="bolder"
                      padded
                    />
                  );
                })}
              </div>
            </div>
          </Card>
        </div>
      </Tag>

      {showAttachmentPreview.show && (
        <AttachmentPreviewer
          showPreviewer={showAttachmentPreview.show}
          closePreviwer={closePreviwer}
          list={attachmentsList}
          selectedIndex={showAttachmentPreview.index}
        />
      )}
    </div>
  );
}

export default withErrorBoundary(injectIntl(SideConversationBubble));
